import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, useRefreshUser, useLogout, useGoogleLogin } from '../api/users';
import { setLogoutCallback } from '../api/client';
import { signInWithRedirect, getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Amplify } from 'aws-amplify';
import awsExports from '../aws-exports';
import LoadingScreen from '../components/LoadingScreen';
import { safeLocalStorage } from '../utils/storage';

interface UserContextType {
  user: User | null;
  isLoggedIn: boolean;
  login: (user: User) => void;
  logout: () => void;
  register: (user: User) => void;
  updateUser: (user: User) => void;
  signInWithGoogle: () => Promise<void>;
}

Amplify.configure(awsExports);

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  // Try to get user info - the axios interceptor will handle token refresh if needed
  const { data: refreshData, error: refreshError } = useRefreshUser();
  const logoutMutation = useLogout();
  const googleLoginMutation = useGoogleLogin();

  // Check if user is logged in
  const isLoggedIn = user !== null;

  // Handle user data from refresh
  useEffect(() => {
    if (refreshData) {
      // Always update user data when refresh data is available
      setUser(refreshData);
    } else if (refreshError) {
      // Clear user data on refresh error
      setUser(null);
      signOut({ global: false });
    }
  }, [refreshData, refreshError]);

  // Register logout callback with API client
  useEffect(() => {
    const logoutCallback = () => {
      setUser(null);
    };
    setLogoutCallback(logoutCallback);
  }, []);

  // Check for existing user session on mount and listen for auth events
  useEffect(() => {
    // Listen for authentication events
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          // Handle Google login callback
          handleGoogleLoginCallback();
          break;
        case 'signedOut':
          setUser(null);
          break;
        default:
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, []);

  // Login function
  const login = (userData: User) => {
    setUser(userData);
  };

  // Logout function
  const logout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        setUser(null);
        // Redirect to the same page with a refresh after successful logout
        signOut({ global: false });
      },
      onError: () => {
        // Even if logout fails on backend, clear local user state
        setUser(null);
        signOut({ global: false });
      }
    });
  };

  const register = (userData: User) => {
    login(userData);
  };

  const updateUser = (user: User) => {
    if (user) {
      setUser(user);
    }
  };

  // Google Sign In function
  const signInWithGoogle = async () => {
    try {
      const currentUrl = window.location.href;
      const returnUrl = new URL(currentUrl).pathname + new URL(currentUrl).search;
      // Store in localStorage so it survives the redirect
      safeLocalStorage.setItem('auth_return_url', returnUrl);

      // Use Amplify to get Google tokens, then send to your backend
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      safeLocalStorage.removeItem('auth_return_url');
      throw error;
    }
  };

  // Handle Google login callback (called after redirect)
  const handleGoogleLoginCallback = async () => {
    try {
      const returnUrl = safeLocalStorage.getItem('auth_return_url');

      const session = await fetchAuthSession();
      if (session) {
        const idToken = session.tokens?.idToken?.toString();
        const accessToken = session.tokens?.accessToken?.toString();

        if (idToken && accessToken) {
          // Send tokens to your backend
          const response = await googleLoginMutation.mutateAsync({
            id_token: idToken,
            access_token: accessToken,
            provider: 'google',
          });

          // Set user data from your backend response
          setUser(response.user);
          if (returnUrl) {
            window.location.href = returnUrl;
            safeLocalStorage.removeItem('auth_return_url');
          }
        }
      }
    } catch (error) {
      setUser(null);
      console.error('Error handling Google login callback:', error);
      throw error;
    }
  };

  const value: UserContextType = {
    user,
    isLoggedIn,
    login,
    logout,
    register,
    updateUser,
    signInWithGoogle,
  };

  if (safeLocalStorage.getItem('auth_return_url') !== null) {
    return <LoadingScreen />;
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
